import { InlineValue, Link, useCategories, usePage, usePageContent, useXcoreQuery } from "@appiodev/xcore-client/xcore-ui";
import { ContentsResponse, only, XcoreCMS } from "@appiodev/xcore-core";
import { Box, Button, Cell, Container, Flex, SimpleGrid } from "@xcorejs/ui";
import Layout from "components/Layout";
import NewsFilter from "components/news/NewsFilter";
import { useCategoryFilter, useFilters, useValueFilter } from "components/news/useFilters";
import { PodcastEpisodeButton } from "components/PodcastEpisodeButton";
import { RssButton } from "components/RssButton";
import NewsCard from "design-system/robe/cards/News";
import RobeHeading from "design-system/robe/RobeHeading";
import { useRouter } from "next/router";
import { useMemo, useRef, useState } from "react";
import { useTagManager } from "utils/useTagManager";
import { getRobeProps, RobePage, useLayout } from "xcore";
import { News, NewsPage, Product } from "xcore/types";

export const getStaticProps = getRobeProps(({ cms, locale }) => ({
  pageContent: "newsPage",
  categories: [
    cms.taxonomy.list("news-categories")
  ],
  contents: [
    getNews(cms, locale ?? "en"),
    cms.content.list<Product>("product", { fields: ["discontinuedGlob", "discontinuedReg"], perPage: 10000 })
  ]
}));

const NewsPagePage: RobePage = () => {
  const { query, locale } = useRouter();
  const [content] = usePageContent<NewsPage>();
  const { data: podcastPage } = useXcoreQuery(cms => cms.content.single("podcastPage")); // temporarily being fetched here, in XCC the fetching is SSR
  const { contents: [news], stringify } = usePage<any, any, [NewsPreview]>();
  const [isRssCopied, setIsRssCopied] = useState<boolean>(false);

  const { data } = useXcoreQuery(cms => getNews(cms, locale ?? "en", query));

  const stickyData = useStickyResult(data, news);

  const filters = useNewsFilters(stickyData!);

  useTagManager("news-listing");

  return (
    <>
      <Container
        flexDirection="column"
        paddingTop={{ _: "3rem", sm: "5rem" }}
        paddingBottom={{ _: "6rem", sm: "4rem" }}
      >
        <Box mb="3rem" display="flex">
          <RobeHeading width="100%"><InlineValue value={stringify(content.values.title)} /></RobeHeading>
          <Box position="relative" display="flex" alignItems="center">
            {podcastPage && (
              <Link type="simple" content={podcastPage.content} role="group">
                <PodcastEpisodeButton position="absolute" right={{ _: 0, sm: isRssCopied ? 150 : 70 }} top={{ _: 35, sm: 11 }} />
              </Link>
            )}
            <RssButton isRssCopied={isRssCopied} setIsRssCopied={setIsRssCopied} position="absolute" right={0} />
          </Box>
        </Box>

        <Box mb="3rem">
          <NewsFilter
            products={filters.products}
            category={filters.category}
            year={filters.years}
            onChange={filters.onChange}
          />
        </Box>

        <Flex mb={{ _: 0, sm: "6rem" }} width="100%" justifyContent="center" alignItems="center" flexDirection="column">
          <SimpleGrid
            gap="3rem"
            columns={{ _: 1, sm: 2, md: 4, lg: 4 }}
            column="minmax(100px, 300px)"
            width={{ _: "auto", lg: "100%" }}
            mb={{ _: "3rem", sm: "6rem" }}
            style={{ gridAutoFlow: "row dense" }}
          >
            {filters?.news?.map(c => (
              <Cell
                key={c.id}
                display="flex"
                justifyContent="center"
                {...stringify(c.values.size) === "3" && { gridColumn: { _: "span 1", sm: "span 2" } }}
              >
                <Link content={c} width="100%">
                  <NewsCard content={c} variant="outline" couldBeLarge zIndex={1} />
                </Link>
              </Cell>
            ))}
          </SimpleGrid>

          {stickyData?.options?.nextContent && (
            <Link
              content={content}
              query={filters.nextPage()}
              noUILink
              scroll={false}
              replace
            >
              <Button as="a">
                <InlineValue value={content.values.btnMore} />
              </Button>
            </Link>
          )}
        </Flex>
      </Container>
    </>
  );
};

NewsPagePage.Layout = Layout;

export default NewsPagePage;

export type XcoreQuery = {
  id?: string;
  xcore?: any;
} & Record<string, string | string[]>;

const parseCategoryIds = (categories: string | string[]) => [Array.isArray(categories) ? categories.map(Number) : [+categories]];

type NewsPreview = News<"title" | "intro" | "images" | "size" | "thumbnail" | "thumbnailIntro" | "content">;

export const getNews = (cms: XcoreCMS, locale: string, { year, product, ...query }: Record<string, string | string[] | undefined> = {}) =>
  (
    locale !== "fr"
      ? cms
      : cms.withCtx({ locales: { content: ["fr"] } })
  ).content.list<News>("news", {
    fields: ["title", "intro", "images", "size", "thumbnail", "content", "thumbnailIntro"],
    sort: ["publishedAt", "desc"],
    includeFiles: true,
    includeCategories: true,
    ...query?.category && { categories: parseCategoryIds(query?.category) },
    ...year ? { publishedAt: [`${year as string}-01-01`, `${Number(year as string) + 1}-01-01`] } : {},
    filterByRelations: (Array.isArray(product) ? product : [product]).filter(x => !isNil(x)).map(Number),
    ...newsLimit(query)
  });

const useNewsFilters = <T extends News<"size">>({ options, contents }: Pick<ContentsResponse<T>, "contents" | "options">) => {
  const { stringify, localize } = useLayout();
  const categories = useCategories();
  const sortedCategories = useMemo(() => [...categories[0]].sort((a, b) => stringify(a.name).localeCompare(stringify(b.name), "en"))
    , [categories, stringify]);

  const { contents: [,p] } = usePage<any, any, [any, Product]>();

  const category = useCategoryFilter("category", sortedCategories);

  const years = useValueFilter(
    "year",
    [...Array(new Date().getFullYear() - 2001)].map((_, i) => ({ label: 2002 + i + "", value: 2002 + i })).reverse()
  );
  const products = useValueFilter(
    "product",
    p.contents
      .filter(x => !x.values.discontinuedGlob && !localize(x.values.discontinuedReg))
      .map((x) => ({ label: stringify(x.title), value: x.id }))
      .sort((x, y) => x.label.localeCompare(y.label))
  );

  const { nextPage, onChange } = useFilters([category, years, products], { shallow: true });

  const news = useMemo(
    () => {
      if (!options?.nextContent) return contents;
      const width = contents.reduce((acc, x) => acc + typeToWidth(stringify(x.values.size ?? "")), 0);
      const toTrim = width % 12;

      let w = 0;
      const result: T[] = [];
      for (const n of contents) {
        w += typeToWidth(stringify(n.values.size ?? ""));
        if (w > width - toTrim) break;
        result.push(n as T);
      }
      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contents, options!.nextContent, localize, stringify]
  );

  return {
    category,
    years,
    products,
    onChange,
    nextPage,
    news
  };
};

const useStickyResult = <T extends any>(value: T, initialValue: T) => {
  const val = useRef<T>(initialValue);
  if (value !== undefined) val.current = value;
  return val.current;
};

const isNil = (x: any): x is null | undefined =>
  x === null || x === undefined;

const newsLimit = (query: Record<string, string | string[] | undefined>) => {
  return {
    page: 1,
    perPage: 12 * (+only(query.page) || 1) + 3
  };
};

const typeToWidth = (type: string) => type === "3" ? 2 : 1;
